// GymOwnerTools.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import GymOwnerForm from "./form-gym-owner";
import { useNavigate, useLocation as useRRLocation } from "react-router-dom";
import GrowthGuideComponent from "./tools/growth-guide";

/** ---- Tool components (swap these with your real ones) ---- */
function GrowthGuide() {
  return <GrowthGuideComponent />;
}
function ChurnCalculator() {
  return <div>📊 Churn Calculator (upload CSV / enter MRR + cancels)</div>;
}
function SemiPrivateTrainingScript() {
  return <div>🗣️ Semi-Private Training enrollment script</div>;
}
function CoachTraining() {
  return <div>🎯 Coach Training checklist + SOP</div>;
}
function LTVCalculator() {
  return <div>💎 LTV Calculator</div>;
}
function SoftPitchOnePager() {
  return <div>💡 Avro one-pager (soft pitch)</div>;
}
function FounderFarewell() {
  return <div>👋 Founder video + final CTA</div>;
}

/** ---- Types and mapping ---- */
type ToolKey =
  | "guide"
  | "churn-calc"
  | "semi-private-training-script"
  | "coach-training"
  | "ltv-calc"
  | "soft-pitch"
  | "farewell";

const TOOL_MAP: Record<
  ToolKey,
  { label: string; Component: React.ComponentType }
> = {
  guide: { label: "Growth Guide", Component: GrowthGuide },
  "churn-calc": { label: "Churn Calculator", Component: ChurnCalculator },
  "semi-private-training-script": {
    label: "Semi-Private Training Script",
    Component: SemiPrivateTrainingScript,
  },
  "coach-training": { label: "Coach Training", Component: CoachTraining },
  "ltv-calc": { label: "LTV Calculator", Component: LTVCalculator },
  "soft-pitch": { label: "Avro One-Pager", Component: SoftPitchOnePager },
  farewell: { label: "Founder Farewell", Component: FounderFarewell },
};

/** ---- Helpers ---- */
function useQuery() {
  const { search } = useRRLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function GymOwnerTools() {
  const location = useRRLocation();
  const query = useQuery();
  const navigate = useNavigate();

  // Refs
  const formEndRef = useRef<HTMLDivElement | null>(null);
  const toolAnchorRef = useRef<HTMLDivElement | null>(null);

  // Dark palette
  const c = {
    text: "var(--text-primary, #e6e9ef)",
    textMuted: "var(--text-muted, #a9b2c3)",
    bg2: "var(--bg-elev-1, #0f141a)",
    border: "var(--border, rgba(255,255,255,0.08))",
    borderStrong: "var(--border-strong, rgba(255,255,255,0.14))",
    pillBg: "var(--pill-bg, rgba(255,255,255,0.04))",
    pillBgActive: "var(--pill-active, rgba(89,255,167,0.12))",
    accent: "var(--accent, #59ffa7)",
  };

  // Current tool
  const toolParam = (query.get("tool") || "guide").toLowerCase() as ToolKey;
  const activeTool: ToolKey = (Object.keys(TOOL_MAP) as ToolKey[]).includes(
    toolParam
  )
    ? toolParam
    : "guide";
  const { Component, label } = useMemo(
    () => ({
      Component: TOOL_MAP[activeTool].Component,
      label: TOOL_MAP[activeTool].label,
    }),
    [activeTool]
  );

  // Normalize URL if missing/invalid tool
  useEffect(() => {
    const current = query.get("tool");
    if (!current || !TOOL_MAP[activeTool]) {
      const params = new URLSearchParams(query);
      params.set("tool", activeTool);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [activeTool, navigate, query]);

  // Title
  useEffect(() => {
    const prev = document.title;
    document.title = `${label} — Gym Owner Tools`;
    return () => {
      document.title = prev;
    };
  }, [label]);

  // --- FAB persistence key: per path + tool ---
  const FAB_KEY = useMemo(
    () => `avro.fabDismissed::${location.pathname}?tool=${activeTool}`,
    [location.pathname, activeTool]
  );

  const REAPPEAR_MS = 10 * 60 * 1000; // 10 minutes
  type FabStore = { dismissed: "1"; ts: number };

  // Debug helpers (?fab=reset | ?fab=show)
  useEffect(() => {
    const fabParam = query.get("fab");
    if (fabParam === "reset") {
      try {
        Object.keys(sessionStorage)
          .filter((k) => k.startsWith("avro.fabDismissed"))
          .forEach((k) => sessionStorage.removeItem(k));
      } catch {}
    }
  }, [query]);

  const initialDismissed = useMemo(() => {
    const forceShow = query.get("fab") === "show";
    if (forceShow) return false;
    try {
      const raw = sessionStorage.getItem(FAB_KEY);
      if (!raw) return false;
      const parsed: FabStore = JSON.parse(raw);
      if (!parsed?.ts) return true;
      const expired = Date.now() - parsed.ts > REAPPEAR_MS;
      return !expired;
    } catch {
      return sessionStorage.getItem(FAB_KEY) === "1";
    }
  }, [FAB_KEY, query]);

  const [dismissed, setDismissed] = useState<boolean>(initialDismissed);

  useEffect(() => {
    setDismissed(initialDismissed);
  }, [FAB_KEY]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistDismiss = () => {
    try {
      const payload: FabStore = { dismissed: "1", ts: Date.now() };
      sessionStorage.setItem(FAB_KEY, JSON.stringify(payload));
    } catch {
      sessionStorage.setItem(FAB_KEY, "1");
    }
  };

  const dismissFab = () => {
    setDismissed(true);
    persistDismiss();
  };

  // A) Dismiss once past form end
  useEffect(() => {
    if (dismissed || !formEndRef.current) return;
    const formEndTopAbs =
      formEndRef.current.getBoundingClientRect().top + window.scrollY;
    const onScroll = () => {
      if (window.scrollY >= formEndTopAbs - 1) dismissFab();
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  // B) Dismiss when tool becomes visible — with a grace delay
  useEffect(() => {
    if (dismissed || !toolAnchorRef.current) return;
    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, 1200);
    const io = new IntersectionObserver(
      (entries) => {
        if (armed && entries[0]?.isIntersecting) dismissFab();
      },
      { threshold: 0.25 }
    );
    io.observe(toolAnchorRef.current);
    return () => {
      clearTimeout(armTimer);
      io.disconnect();
    };
  }, [dismissed]);

  // C) Dismiss if loaded already past the form end
  useLayoutEffect(() => {
    if (dismissed) return;
    const formEndTopAbs =
      (formEndRef.current?.getBoundingClientRect().top ?? Infinity) +
      window.scrollY;
    if (window.scrollY >= formEndTopAbs - 1) {
      dismissFab();
    }
  }, [dismissed]);

  const handleJumpToTool = () => {
    toolAnchorRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    dismissFab();
  };

  return (
    <div
      style={{
        maxWidth: 980,
        margin: "0 auto",
        padding: "24px",
        color: c.text,
      }}
    >
      {/* keep shared styles for non-FAB bits */}
      <style>{`
        .tool-pill { padding: 8px 10px; border-radius: 10px; border: 1px solid ${c.border};
          background: ${c.pillBg}; color: ${c.text}; text-decoration: none;
          transition: background 150ms ease, border-color 150ms ease; }
        .tool-pill:hover { background: ${c.pillBgActive}; border-color: ${c.borderStrong}; }
        .tool-pill.active { background: ${c.pillBgActive}; border-color: ${c.borderStrong}; }
        .divider { margin: 24px 0; border: 0; border-top: 1px solid ${c.border}; }
      `}</style>

      {/* Form */}
      <GymOwnerForm />

      {/* FAB container (centered) */}
      <div
        style={{
          width: "100%",
          marginTop: -128,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 0",
        }}
      >
        <button
          onClick={handleJumpToTool}
          aria-label={`Jump to ${label}`}
          style={{
            padding: "12px 16px",
            borderRadius: 14,
            border: `1px solid ${c.borderStrong}`,
            background: c.bg2,
            color: c.text,
            display: "flex",
            alignItems: "center",
            gap: 8, // (was 10)
            boxShadow: "0 10px 30px rgba(0,0,0,.35)",
            cursor: "pointer",
            outline: "none",
            zIndex: 0,
          }}
        >
          <span style={{ color: c.textMuted }}>Keep Scrolling</span>
          <span
            aria-hidden="true"
            className="keep-scroll-arrows"
            style={{
              display: "inline-flex",
              flexDirection: "column",
              lineHeight: 0,
              color: c.accent, // green
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 9l5 5 5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              style={{ marginTop: -6 }}
            >
              <path
                d="M7 9l5 5 5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </div>

      {/* Tool section */}
      <div ref={toolAnchorRef} id="tool" style={{ marginTop: 128 }}>
        <Component />
      </div>

      {/* Tool switcher */}
      <div style={{ marginTop: 24, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(Object.keys(TOOL_MAP) as ToolKey[]).map((key) => {
          const params = new URLSearchParams(query);
          params.set("tool", key);
          const isActive = key === activeTool;
          return (
            <a
              key={key}
              href={`?${params.toString()}`}
              className={`tool-pill ${isActive ? "active" : ""}`}
            >
              {TOOL_MAP[key].label}
            </a>
          );
        })}
      </div>
    </div>
  );
}
